import type {
  WarehouseBkMatchItem,
  WarehouseInvoiceSummary,
  WarehouseLedgerTransactionItem,
} from '@/services/warehouseTypes'
import { normalizeBulkSku } from '@/utils/normalizeBulkSku'
import { txnDateMonthKey } from '@/utils/warehouseProcessMonth'

export type FlowMode = 'qty' | 'dollar'

export type CatalogStockRow = {
  sku: string
  qty: number
  value: number
  product?: string | null
}

export type FlowStockBySku = {
  bulk_sku: string
  product?: string | null
  qty_remaining: number
  inventory_value: number
  weighted_unit_cost: number
}

export type FlowInflowAggregate = {
  invoiceKey: string
  invoiceLabel: string
  invoiceDate: string | null
  bulk_sku: string
  value: number
  unitCount: number
}

export type FlowOutflowAggregate = {
  destination: string
  bulk_sku: string
  value: number
  unitCount: number
}

export type FlowScope = 'month' | 'cumulative'

export type FlowSkuAmount = {
  qty: number
  value: number
}

export type FlowStoreDraw = {
  destination: string
  qty: number
  value: number
}

export type FlowPeriodSkuRow = {
  bulk_sku: string
  opening: FlowSkuAmount
  purchases: FlowSkuAmount
  /** Net period manual_adjustment (signed qty/value). */
  adjustments: FlowSkuAmount
  storeDraws: FlowStoreDraw[]
  ending: FlowSkuAmount
}

export type OrderInvoiceRef = {
  invoiceNumber: string
  invoiceDate: string | null
}

const STORE_TRANSFER_INVOICE_KEY = 'store-transfer'

export function isActiveLedgerRow(row: WarehouseLedgerTransactionItem): boolean {
  return !row.voided_at
}

export function isLedgerInflow(row: WarehouseLedgerTransactionItem): boolean {
  if (!isActiveLedgerRow(row)) return false
  if (row.kind === 'store_return') return true
  return row.kind === 'manufacturer_purchase' || row.kind === 'initial_store_transfer'
}

export function isLedgerOutflow(row: WarehouseLedgerTransactionItem): boolean {
  if (!isActiveLedgerRow(row)) return false
  return row.kind === 'store_draw'
}

export type LedgerAdjustmentSummary = {
  count: number
  positiveCases: number
  negativeCases: number
}

export function summarizeLedgerAdjustments(
  rows: WarehouseLedgerTransactionItem[],
  month?: string | null
): LedgerAdjustmentSummary {
  let count = 0
  let positiveCases = 0
  let negativeCases = 0

  for (const row of rows) {
    if (!isActiveLedgerRow(row) || row.kind !== 'manual_adjustment') continue
    if (month && txnDateMonthKey(row.txn_date) !== month) continue

    const cases = Number(row.signed_cases) || 0
    if (cases === 0) continue

    count += 1
    if (cases > 0) {
      positiveCases += cases
    } else {
      negativeCases += Math.abs(cases)
    }
  }

  return { count, positiveCases, negativeCases }
}

export function ledgerFlowQty(row: WarehouseLedgerTransactionItem): number {
  return Math.abs(Number(row.signed_cases) || 0)
}

export function ledgerFlowValue(row: WarehouseLedgerTransactionItem, mode: FlowMode): number {
  const qty = ledgerFlowQty(row)
  if (mode === 'qty') return qty
  return qty * (Number(row.unit_cost) || 0)
}

export function filterActiveLedgerRows(
  rows: WarehouseLedgerTransactionItem[]
): WarehouseLedgerTransactionItem[] {
  return rows.filter(isActiveLedgerRow)
}

export function filterLedgerInflowsForMonth(
  rows: WarehouseLedgerTransactionItem[],
  month: string
): WarehouseLedgerTransactionItem[] {
  if (!month) return []
  return rows.filter(
    (row) => isLedgerInflow(row) && txnDateMonthKey(row.txn_date) === month
  )
}

export function filterLedgerOutflowsForMonth(
  rows: WarehouseLedgerTransactionItem[],
  month: string
): WarehouseLedgerTransactionItem[] {
  if (!month) return []
  return rows.filter(
    (row) => isLedgerOutflow(row) && txnDateMonthKey(row.txn_date) === month
  )
}

export function collectLedgerFlowMonths(rows: WarehouseLedgerTransactionItem[]): string[] {
  const months = new Set<string>()
  for (const row of rows) {
    if (!isActiveLedgerRow(row)) continue
    if (!isLedgerInflow(row) && !isLedgerOutflow(row)) continue
    const key = txnDateMonthKey(row.txn_date)
    if (key) months.add(key)
  }
  return [...months].sort()
}

function formatInvoiceDate(raw?: string | null): string | null {
  if (!raw) return null
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(raw)
  if (iso?.[1]) return iso[1]
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return null
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
}

/** Map Bright Sites BK order number → Halo invoice number + date (PO / match). */
export function buildOrderInvoiceMap(
  invoices: WarehouseInvoiceSummary[],
  bkMatches: WarehouseBkMatchItem[]
): Map<string, OrderInvoiceRef> {
  const byNumber = new Map<string, string | null>()
  for (const invoice of invoices) {
    byNumber.set(invoice.invoice_number, formatInvoiceDate(invoice.invoice_date))
  }

  const map = new Map<string, OrderInvoiceRef>()
  for (const invoice of invoices) {
    const po = invoice.po_number?.trim()
    if (!po) continue
    map.set(po, {
      invoiceNumber: invoice.invoice_number,
      invoiceDate: formatInvoiceDate(invoice.invoice_date),
    })
  }
  for (const match of bkMatches) {
    const order = (match.order_number || match.po_number)?.trim()
    if (!order || !match.invoice_number) continue
    map.set(order, {
      invoiceNumber: match.invoice_number,
      invoiceDate: byNumber.get(match.invoice_number) ?? null,
    })
  }
  return map
}

export function resolveInvoiceForLedgerTxn(
  row: WarehouseLedgerTransactionItem,
  orderInvoiceMap: Map<string, OrderInvoiceRef>,
  productInvoiceNumberByLineId?: Map<number, string>
): { invoiceKey: string; invoiceLabel: string; invoiceDate: string | null } {
  if (row.kind === 'initial_store_transfer') {
    const store = row.store?.trim()
    return {
      invoiceKey: STORE_TRANSFER_INVOICE_KEY,
      invoiceLabel: store ? `Store transfer (${store})` : 'Store transfer',
      invoiceDate: formatInvoiceDate(row.txn_date),
    }
  }

  if (row.product_invoice_line_id != null) {
    const invoiceNumber =
      productInvoiceNumberByLineId?.get(row.product_invoice_line_id) ??
      `line:${row.product_invoice_line_id}`
    return {
      invoiceKey: invoiceNumber,
      invoiceLabel: invoiceNumber,
      invoiceDate: formatInvoiceDate(row.txn_date),
    }
  }

  const order = row.order_number?.trim()
  if (order) {
    const ref = orderInvoiceMap.get(order)
    if (ref) {
      return {
        invoiceKey: ref.invoiceNumber,
        invoiceLabel: ref.invoiceNumber,
        invoiceDate: ref.invoiceDate,
      }
    }
    return {
      invoiceKey: `order:${order}`,
      invoiceLabel: `Order ${order}`,
      invoiceDate: formatInvoiceDate(row.txn_date),
    }
  }

  return {
    invoiceKey: 'unknown',
    invoiceLabel: 'Unknown source',
    invoiceDate: formatInvoiceDate(row.txn_date),
  }
}

export function aggregateLedgerInflows(
  rows: WarehouseLedgerTransactionItem[],
  mode: FlowMode,
  orderInvoiceMap: Map<string, OrderInvoiceRef>,
  productInvoiceNumberByLineId?: Map<number, string>
): FlowInflowAggregate[] {
  const byKey = new Map<string, FlowInflowAggregate>()

  for (const row of rows) {
    if (!isLedgerInflow(row)) continue
    const value = ledgerFlowValue(row, mode)
    const unitCount = ledgerFlowQty(row)
    if (value <= 0) continue

    const sku = normalizeBulkSku(row.bk_sku)
    if (!sku) continue

    const { invoiceKey, invoiceLabel, invoiceDate } = resolveInvoiceForLedgerTxn(
      row,
      orderInvoiceMap,
      productInvoiceNumberByLineId
    )
    const key = `${invoiceKey}|${sku}`
    const existing = byKey.get(key)
    if (existing) {
      existing.value += value
      existing.unitCount += unitCount
      if (!existing.invoiceDate && invoiceDate) existing.invoiceDate = invoiceDate
    } else {
      byKey.set(key, {
        invoiceKey,
        invoiceLabel,
        invoiceDate,
        bulk_sku: sku,
        value,
        unitCount,
      })
    }
  }

  return [...byKey.values()]
}

export function aggregateLedgerOutflows(
  rows: WarehouseLedgerTransactionItem[],
  mode: FlowMode
): FlowOutflowAggregate[] {
  const byKey = new Map<string, FlowOutflowAggregate>()

  for (const row of rows) {
    if (!isLedgerOutflow(row)) continue
    const value = ledgerFlowValue(row, mode)
    const unitCount = ledgerFlowQty(row)
    if (value <= 0) continue

    const sku = normalizeBulkSku(row.bk_sku)
    if (!sku) continue

    const destination = row.store?.trim() || 'Unknown store'
    const key = `${destination}|${sku}`
    const existing = byKey.get(key)
    if (existing) {
      existing.value += value
      existing.unitCount += unitCount
    } else {
      byKey.set(key, {
        destination,
        bulk_sku: sku,
        value,
        unitCount,
      })
    }
  }

  return [...byKey.values()]
}

export function buildProductNameMapFromLedger(
  rows: WarehouseLedgerTransactionItem[],
  displayNames?: Map<string, string>
): Map<string, string> {
  const map = new Map<string, string>()
  if (displayNames) {
    for (const [sku, name] of displayNames) {
      if (name.trim()) map.set(normalizeBulkSku(sku), name.trim())
    }
  }
  for (const row of rows) {
    const sku = normalizeBulkSku(row.bk_sku)
    const name = row.product_name?.trim()
    if (!sku || !name || map.has(sku)) continue
    map.set(sku, name)
  }
  return map
}

function emptyFlowAmount(): FlowSkuAmount {
  return { qty: 0, value: 0 }
}

function addFlowAmount(target: FlowSkuAmount, qty: number, value: number) {
  target.qty += qty
  target.value += value
}

function ledgerSignedQty(row: WarehouseLedgerTransactionItem): number {
  return Number(row.signed_cases) || 0
}

function ledgerSignedValue(row: WarehouseLedgerTransactionItem, mode: FlowMode): number {
  const qty = ledgerSignedQty(row)
  if (mode === 'qty') return qty
  return qty * (Number(row.unit_cost) || 0)
}

/** True when txn_date falls strictly before the first day of `month` (YYYY-MM). */
export function isTxnDateStrictlyBeforeMonth(
  txnDate: string | null | undefined,
  month: string
): boolean {
  if (!month || !txnDate) return false
  const key = txnDateMonthKey(txnDate)
  if (!key) return false
  return key < month
}

/** Net signed qty / valuation from all active ledger rows before month start. */
export function computeOpeningBySku(
  rows: WarehouseLedgerTransactionItem[],
  month: string,
  mode: FlowMode
): Map<string, FlowSkuAmount> {
  const bySku = new Map<string, FlowSkuAmount>()

  for (const row of rows) {
    if (!isActiveLedgerRow(row)) continue
    if (!isTxnDateStrictlyBeforeMonth(row.txn_date, month)) continue

    const sku = normalizeBulkSku(row.bk_sku)
    if (!sku) continue

    const existing = bySku.get(sku) ?? emptyFlowAmount()
    addFlowAmount(
      existing,
      ledgerSignedQty(row),
      ledgerSignedValue(row, mode)
    )
    bySku.set(sku, existing)
  }

  return bySku
}

/** Period purchases (manufacturer_purchase + initial_store_transfer) aggregated per SKU. */
export function aggregatePurchasesBySku(
  rows: WarehouseLedgerTransactionItem[],
  mode: FlowMode,
  month?: string | null
): Map<string, FlowSkuAmount> {
  const bySku = new Map<string, FlowSkuAmount>()

  for (const row of rows) {
    if (!isLedgerInflow(row)) continue
    if (month && txnDateMonthKey(row.txn_date) !== month) continue

    const sku = normalizeBulkSku(row.bk_sku)
    if (!sku) continue

    const qty = ledgerFlowQty(row)
    const value = ledgerFlowValue(row, mode)
    if (qty <= 0 && value <= 0) continue

    const existing = bySku.get(sku) ?? emptyFlowAmount()
    addFlowAmount(existing, qty, value)
    bySku.set(sku, existing)
  }

  return bySku
}

/** Period store_draw rows aggregated per SKU and destination. */
export function aggregateStoreDrawsBySku(
  rows: WarehouseLedgerTransactionItem[],
  mode: FlowMode,
  month?: string | null
): Map<string, FlowStoreDraw[]> {
  const bySkuStore = new Map<string, Map<string, FlowStoreDraw>>()

  for (const row of rows) {
    if (!isLedgerOutflow(row)) continue
    if (month && txnDateMonthKey(row.txn_date) !== month) continue

    const sku = normalizeBulkSku(row.bk_sku)
    if (!sku) continue

    const qty = ledgerFlowQty(row)
    const value = ledgerFlowValue(row, mode)
    if (qty <= 0 && value <= 0) continue

    const destination = row.store?.trim() || 'Unknown store'
    const storeMap = bySkuStore.get(sku) ?? new Map<string, FlowStoreDraw>()
    const existing = storeMap.get(destination) ?? { destination, qty: 0, value: 0 }
    existing.qty += qty
    existing.value += value
    storeMap.set(destination, existing)
    bySkuStore.set(sku, storeMap)
  }

  const result = new Map<string, FlowStoreDraw[]>()
  for (const [sku, storeMap] of bySkuStore) {
    result.set(
      sku,
      [...storeMap.values()].sort((a, b) => a.destination.localeCompare(b.destination))
    )
  }
  return result
}

/** Period manual_adjustment net per SKU (signed qty / signed $). */
export function aggregateAdjustmentsBySku(
  rows: WarehouseLedgerTransactionItem[],
  mode: FlowMode,
  month?: string | null
): Map<string, FlowSkuAmount> {
  const bySku = new Map<string, FlowSkuAmount>()

  for (const row of rows) {
    if (!isActiveLedgerRow(row) || row.kind !== 'manual_adjustment') continue
    if (month && txnDateMonthKey(row.txn_date) !== month) continue

    const sku = normalizeBulkSku(row.bk_sku)
    if (!sku) continue

    const qty = ledgerSignedQty(row)
    const value = ledgerSignedValue(row, mode)
    if (qty === 0 && value === 0) continue

    const existing = bySku.get(sku) ?? emptyFlowAmount()
    addFlowAmount(existing, qty, value)
    bySku.set(sku, existing)
  }

  return bySku
}

export function flowMetric(amount: FlowSkuAmount, mode: FlowMode): number {
  return mode === 'qty' ? amount.qty : amount.value
}

export function drawMetric(draw: FlowStoreDraw, mode: FlowMode): number {
  return mode === 'qty' ? draw.qty : draw.value
}

function hasPositiveFlowMetric(amount: FlowSkuAmount, mode: FlowMode): boolean {
  return flowMetric(amount, mode) > 0
}

export type FlowRowSort = 'activity' | 'name' | 'ending'

export type FilterPeriodFlowOptions = {
  search?: string
  /** When non-empty, only SKUs that drew to at least one selected store. */
  stores?: string[]
  mode: FlowMode
}

export function periodFlowActivityScore(row: FlowPeriodSkuRow, mode: FlowMode): number {
  const purchases = Math.abs(flowMetric(row.purchases, mode))
  const adjustments = Math.abs(flowMetric(row.adjustments, mode))
  const draws = row.storeDraws.reduce((sum, draw) => sum + Math.abs(drawMetric(draw, mode)), 0)
  return purchases + adjustments + draws
}

export function skuHasPeriodActivity(row: FlowPeriodSkuRow, mode: FlowMode): boolean {
  if (flowMetric(row.purchases, mode) > 0) return true
  if (flowMetric(row.adjustments, mode) !== 0) return true
  return row.storeDraws.some((draw) => drawMetric(draw, mode) > 0)
}

export function filterPeriodFlowRows(
  rows: FlowPeriodSkuRow[],
  options: FilterPeriodFlowOptions,
  productNames?: Map<string, string>
): FlowPeriodSkuRow[] {
  let result = rows

  if (options.stores && options.stores.length > 0) {
    const storeSet = new Set(options.stores)
    result = result.filter((row) =>
      row.storeDraws.some(
        (draw) => storeSet.has(draw.destination) && drawMetric(draw, options.mode) > 0
      )
    )
  }

  const search = options.search?.trim().toLowerCase()
  if (search) {
    result = result.filter((row) => {
      const sku = row.bulk_sku.toLowerCase()
      const name = productNames?.get(row.bulk_sku)?.toLowerCase() ?? ''
      return sku.includes(search) || name.includes(search)
    })
  }

  return result
}

export function sortPeriodFlowRows(
  rows: FlowPeriodSkuRow[],
  sort: FlowRowSort,
  mode: FlowMode,
  productNames?: Map<string, string>
): FlowPeriodSkuRow[] {
  const sorted = [...rows]
  if (sort === 'activity') {
    sorted.sort((a, b) => periodFlowActivityScore(b, mode) - periodFlowActivityScore(a, mode))
  } else if (sort === 'ending') {
    sorted.sort((a, b) => flowMetric(b.ending, mode) - flowMetric(a.ending, mode))
  } else {
    sorted.sort((a, b) => {
      const labelA = productNames?.get(a.bulk_sku) ?? a.bulk_sku
      const labelB = productNames?.get(b.bulk_sku) ?? b.bulk_sku
      return labelA.localeCompare(labelB)
    })
  }
  return sorted
}

export function collectStoresFromPeriodFlow(rows: FlowPeriodSkuRow[]): string[] {
  const stores = new Set<string>()
  for (const row of rows) {
    for (const draw of row.storeDraws) {
      if (draw.destination) stores.add(draw.destination)
    }
  }
  return [...stores].sort((a, b) => a.localeCompare(b))
}

/**
 * Per-SKU stock-and-flow for a month or cumulative scope.
 * Qty identity (when data is consistent):
 * opening + purchases + adjustments = sum(draws) + ending
 */
export function buildPeriodFlowModel(
  rows: WarehouseLedgerTransactionItem[],
  scope: FlowScope,
  flowMonth: string,
  mode: FlowMode
): FlowPeriodSkuRow[] {
  const activeRows = filterActiveLedgerRows(rows)
  const monthFilter = scope === 'month' ? flowMonth || null : null

  const openingBySku =
    scope === 'month' && flowMonth
      ? computeOpeningBySku(activeRows, flowMonth, mode)
      : new Map<string, FlowSkuAmount>()

  const purchasesBySku = aggregatePurchasesBySku(activeRows, mode, monthFilter)
  const adjustmentsBySku = aggregateAdjustmentsBySku(activeRows, mode, monthFilter)
  const drawsBySku = aggregateStoreDrawsBySku(activeRows, mode, monthFilter)

  const allSkus = new Set<string>()
  for (const sku of openingBySku.keys()) allSkus.add(sku)
  for (const sku of purchasesBySku.keys()) allSkus.add(sku)
  for (const sku of adjustmentsBySku.keys()) allSkus.add(sku)
  for (const sku of drawsBySku.keys()) allSkus.add(sku)

  const result: FlowPeriodSkuRow[] = []

  for (const bulk_sku of [...allSkus].sort((a, b) => a.localeCompare(b))) {
    const opening = openingBySku.get(bulk_sku) ?? emptyFlowAmount()
    const purchases = purchasesBySku.get(bulk_sku) ?? emptyFlowAmount()
    const adjustments = adjustmentsBySku.get(bulk_sku) ?? emptyFlowAmount()
    const storeDraws = drawsBySku.get(bulk_sku) ?? []

    const totalDrawQty = storeDraws.reduce((sum, draw) => sum + draw.qty, 0)
    const totalDrawValue = storeDraws.reduce((sum, draw) => sum + draw.value, 0)

    const ending = emptyFlowAmount()
    const endingQty = opening.qty + purchases.qty + adjustments.qty - totalDrawQty
    const endingValue = opening.value + purchases.value + adjustments.value - totalDrawValue
    if (endingQty > 0) {
      ending.qty = endingQty
      ending.value = endingValue > 0 ? endingValue : endingQty
    }

    const hasActivity =
      opening.qty > 0 ||
      hasPositiveFlowMetric(purchases, mode) ||
      flowMetric(adjustments, mode) !== 0 ||
      storeDraws.some((draw) => (mode === 'qty' ? draw.qty : draw.value) > 0) ||
      ending.qty > 0

    if (!hasActivity) continue

    result.push({
      bulk_sku,
      // Always retain opening (including 0) so the waterfall can show a zero column.
      opening: scope === 'month' ? opening : emptyFlowAmount(),
      purchases,
      adjustments,
      storeDraws,
      ending,
    })
  }

  return result
}

export function catalogStockToFlowRows(
  catalogStock: CatalogStockRow[],
  ledgerRows: WarehouseLedgerTransactionItem[]
): FlowStockBySku[] {
  const bySku = new Map<string, FlowStockBySku>()

  for (const item of catalogStock) {
    const sku = normalizeBulkSku(item.sku)
    if (!sku) continue
    bySku.set(sku, {
      bulk_sku: sku,
      product: item.product,
      qty_remaining: Number(item.qty) || 0,
      inventory_value: Number(item.value) || 0,
      weighted_unit_cost:
        item.qty > 0 ? (Number(item.value) || 0) / Number(item.qty) : 0,
    })
  }

  for (const row of filterActiveLedgerRows(ledgerRows)) {
    const sku = normalizeBulkSku(row.bk_sku)
    if (!sku || bySku.has(sku)) continue
    bySku.set(sku, {
      bulk_sku: sku,
      product: row.product_name,
      qty_remaining: 0,
      inventory_value: 0,
      weighted_unit_cost: 0,
    })
  }

  return [...bySku.values()].sort((a, b) => a.bulk_sku.localeCompare(b.bulk_sku))
}
